import * as React from 'react';
import { ClockIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Split an `HH:MM` string into its parts (empty parts when unset/partial). */
function splitTime(value: string | undefined): { hh: string; mm: string } {
  const [hh = '', mm = ''] = (value ?? '').split(':');
  return { hh, mm };
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Minutes in a day — the rank `00:00` takes on an end-of-day field. */
const MINUTES_PER_DAY = 24 * 60;

/** The `HH:MM` that reads as midnight at the end of a day rather than its start. */
const END_OF_DAY = '00:00';

/** `HH:MM` → minutes since midnight, or null when unparseable. */
function toMinutes(value: string | undefined): number | null {
  if (value === '24:00') return MINUTES_PER_DAY;
  if (!value || !HHMM.test(value)) return null;
  const [h, m] = value.split(':');
  return Number(h) * 60 + Number(m);
}

/**
 * When the hour changes, keep the current minute if the new `hour:minute` is
 * still in range; otherwise snap to the first minute of that hour that is.
 */
function firstValidMinute(
  hour: number,
  currentMinute: string,
  inRange: (total: number) => boolean,
): string {
  const current = currentMinute === '' ? 0 : Number(currentMinute);
  if (inRange(hour * 60 + current)) return currentMinute === '' ? '00' : currentMinute;
  for (let m = 0; m < 60; m += 1) if (inRange(hour * 60 + m)) return pad(m);
  return currentMinute; // no valid minute (hour is fully disabled); leave as-is
}

export interface TimePickerProps {
  /** Current value as `HH:MM` (empty/undefined when unset). */
  value?: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Lower bound (`HH:MM`, exclusive): times at or before this are disabled.
   * Use for a "closing" field so it can't be set at/before "opening".
   */
  minTime?: string | undefined;
  /**
   * Upper bound (`HH:MM`, exclusive): times at or after this are disabled.
   * Use for an "opening" field so it can't be set at/after "closing".
   *
   * A `maxTime` of `00:00` is read as midnight at the END of the day — i.e. no
   * upper bound at all. Taken literally it is 0 minutes, which disables every
   * hour and leaves an opening field with nothing to pick once its closing
   * time is set to midnight.
   */
  maxTime?: string | undefined;
  /**
   * Treat `00:00` as midnight at the END of the day (1440) rather than its
   * start (0).
   *
   * Set on a "closing time" field. A venue open `08:00 - 00:00` closes at
   * midnight, but read as a same-day ordinal `00:00` is 0 minutes and so sorts
   * before every opening time — which made `minTime` disable the one value the
   * merchant was trying to pick. With this on, `00:00` is the latest offerable
   * time instead of the earliest.
   */
  allowEndOfDay?: boolean;
  /** Offer an explicit 24:00 endpoint; 24:01–24:59 are never valid. */
  allow24HourEnd?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/**
 * Time picker with a filled-pill trigger and a two-column (hours / minutes)
 * dropdown, styled with design tokens — a token-backed replacement for the
 * off-brand native `<input type="time">` OS popup. The value is a canonical
 * `HH:MM` string; picking an hour before a minute (or vice-versa) fills the
 * missing half with `00`.
 */
export function TimePicker({
  value,
  onChange,
  onBlur,
  placeholder = '--:--',
  disabled = false,
  minTime,
  maxTime,
  allowEndOfDay = false,
  allow24HourEnd = false,
  className,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: TimePickerProps) {
  const { hh, mm } = splitTime(value);
  const hasValue = Boolean(value);

  const min = toMinutes(minTime);
  // `00:00` as an upper bound means end-of-day, which bounds nothing.
  const max = maxTime === END_OF_DAY ? null : toMinutes(maxTime);

  // `00:00` on an end-of-day field is the LAST minute of the day, not the
  // first — so it ranks above every other time rather than below them.
  const rank = (total: number): number =>
    allowEndOfDay && total === 0 ? MINUTES_PER_DAY : total;

  // A candidate time is allowed when it sits strictly inside (min, max).
  const inRange = (total: number): boolean => {
    if (total > MINUTES_PER_DAY || (total === MINUTES_PER_DAY && !allow24HourEnd))
      return false;
    const value = rank(total);
    return (min === null || value > min) && (max === null || value < max);
  };

  // Hours column: an hour is offered only if SOME minute in it is in range.
  const isHourDisabled = (h: number): boolean => {
    for (let m = 0; m < 60; m += 1) if (inRange(h * 60 + m)) return false;
    return true;
  };
  // Minutes column: judged against the currently-selected hour (0 if unset).
  const activeHour = hh === '' ? 0 : Number(hh);
  const isMinuteDisabled = (m: number): boolean => !inRange(activeHour * 60 + m);

  const commit = (nextHh: string, nextMm: string): void => {
    if (nextHh === '24') {
      if (allow24HourEnd) onChange('24:00');
      return;
    }
    onChange(`${nextHh === '' ? '00' : nextHh}:${nextMm === '' ? '00' : nextMm}`);
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) onBlur?.();
      }}
    >
      <PopoverTrigger
        type="button"
        id={id}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className={cn(
          // Figma time input: the h-10 bordered pill with a trailing 20px
          // clock, 14px regular text/placeholder.
          'flex h-10 w-full items-center gap-2 rounded-full border border-input-border bg-transparent px-4 text-sm font-normal tracking-[-0.28px] text-input-fg shadow-field transition-[color,box-shadow] outline-none focus-visible:border-input-border-focus focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:border-form-control-border-disabled disabled:bg-form-control-bg-disabled disabled:text-form-control-fg-disabled aria-invalid:border-input-border-invalid aria-invalid:ring-input-border-invalid/20',
          className,
        )}
      >
        <span className={cn('flex-1 text-left', !hasValue && 'text-input-placeholder')}>
          {hasValue ? value : placeholder}
        </span>
        <ClockIcon className="size-5 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        portal={false}
        className="w-auto p-0"
      >
        <div className="flex h-52" role="group" aria-label="Time">
          <TimeColumn
            options={allow24HourEnd ? [...HOURS, '24'] : HOURS}
            selected={hh}
            ariaLabel="Hours"
            isDisabled={(opt) => isHourDisabled(Number(opt))}
            onSelect={(next) => commit(next, firstValidMinute(Number(next), mm, inRange))}
          />
          <div className="w-px bg-border" />
          <TimeColumn
            options={MINUTES}
            selected={mm}
            ariaLabel="Minutes"
            isDisabled={(opt) => isMinuteDisabled(Number(opt))}
            onSelect={(next) => commit(hh, next)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TimeColumn({
  options,
  selected,
  ariaLabel,
  isDisabled,
  onSelect,
}: {
  options: string[];
  selected: string;
  ariaLabel: string;
  isDisabled?: (option: string) => boolean;
  onSelect: (value: string) => void;
}) {
  const selectedRef = React.useRef<HTMLButtonElement | null>(null);

  // Scroll the active value into view whenever the column (re)opens.
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div
      role="listbox"
      aria-label={ariaLabel}
      className="scrollbar-always scroll-fade-y w-20 overflow-y-auto overscroll-contain px-1 py-6"
    >
      {options.map((option) => {
        const isSelected = option === selected;
        const optionDisabled = isDisabled?.(option) ?? false;
        return (
          <button
            key={option}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={optionDisabled}
            ref={isSelected ? selectedRef : null}
            onClick={() => onSelect(option)}
            className={cn(
              'flex w-full items-center justify-center rounded-lg py-2 text-base tabular-nums transition-colors',
              optionDisabled
                ? 'cursor-not-allowed text-muted-foreground/40'
                : isSelected
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : 'text-foreground hover:bg-muted',
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
